/**
 * Pure progress arithmetic for an import job. Core carries no React Query,
 * so web and mobile each write their own hook and share these rules — which
 * is what keeps "when do we stop polling" from drifting between platforms.
 */
import type { ImportJobDto } from '@splitfiesta/shared';

export const IMPORT_POLL_INTERVAL_MS = 2000;
const MAX_RESUMES = 3;
/** A job id that will never answer again: gone, expired, or not ours. */
const PERMANENT_STATUSES = new Set([403, 404, 410]);

/** React Query's `QueryStatus`, spelled out so core stays free of the dep. */
export type ImportQueryStatus = 'pending' | 'error' | 'success';

export function isImportTerminal(status: ImportJobDto['status']): boolean {
  return status === 'done' || status === 'failed';
}

export function importPollInterval(
  job: ImportJobDto | undefined
): number | false {
  if (job && isImportTerminal(job.status)) return false;
  return IMPORT_POLL_INTERVAL_MS;
}

export function importProgressPercent(job: ImportJobDto | undefined): number {
  if (!job || job.counters.total === 0) return 0;
  const handled = job.counters.created + job.counters.failed;
  return Math.min(100, Math.round((handled / job.counters.total) * 100));
}

/**
 * `stalled` is the server's own verdict (a queued OR running job whose
 * updatedAt has aged past its window), so never re-derive it from `status`.
 */
export function canResumeImport(job: ImportJobDto | undefined): boolean {
  if (!job || job.status === 'done') return false;
  if (job.resumeCount >= MAX_RESUMES) return false;
  return job.stalled || job.status === 'failed';
}

export function isPermanentImportError(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status !== undefined && PERMANENT_STATUSES.has(status);
}

/**
 * Polling stops on a terminal job, on a query that errored with nothing to
 * show, and on an error the job id can never recover from. Anything else on
 * top of progress we already painted is a blip in a job that keeps running
 * server-side, so the screen keeps ticking instead of freezing at whatever
 * percentage the last good poll left behind.
 */
export function importRefetchInterval(
  status: ImportQueryStatus,
  job: ImportJobDto | undefined,
  error: unknown
): number | false {
  if (status === 'error' && (!job || isPermanentImportError(error)))
    return false;
  return importPollInterval(job);
}
